/**
 * Non-component tokens for the authentication forms: surface classes, the
 * password strength scale, and the scorer behind it. Kept apart from
 * `auth-primitives.tsx` so that file exports components only.
 */

/* ── Surfaces ───────────────────────────────────────────────── */

export const authCardClass =
  "rounded-3xl border border-stone-200/80 bg-white/85 shadow-sm backdrop-blur-sm dark:border-stone-800 dark:bg-stone-900/70";

/**
 * Every form roots itself at this width. The app shell already caps its
 * column at `max-w-md`, so this is a no-op there — but the marketing site
 * renders these forms straight into a full-width `<main>`, and without it the
 * inputs stretch across a 27" monitor.
 */
export const authFormWidthClass = "mx-auto w-full max-w-md";

export const authInputClass =
  "h-11 w-full rounded-xl border-stone-200/90 bg-stone-50/70 pl-10 text-[15px] shadow-sm transition-all duration-200 placeholder:text-stone-400 hover:border-stone-300 hover:bg-white focus:border-primary/50 focus:bg-white focus:ring-4 focus:ring-primary/10 dark:border-stone-800 dark:bg-stone-900/60 dark:hover:border-stone-700 dark:hover:bg-stone-900 dark:focus:bg-stone-900";

export const authPrimaryButtonClass =
  "group relative h-11 w-full overflow-hidden rounded-xl bg-gradient-to-r from-primary to-secondary text-[15px] font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-all duration-200 hover:shadow-lg hover:shadow-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-[0.985] disabled:pointer-events-none disabled:opacity-70";

export const authLinkClass =
  "font-medium text-primary underline-offset-4 transition-colors hover:text-primary/80 hover:underline";

/* ── Password strength ──────────────────────────────────────── */

/** The four-level bar under the password field. */
export type StrengthLevel = 0 | 1 | 2 | 3 | 4;

export const strengthLabel: Record<StrengthLevel, string> = {
  0: "",
  1: "Weak",
  2: "Fair",
  3: "Strong",
  4: "Excellent",
};

export const strengthBarClass: Record<StrengthLevel, string> = {
  0: "bg-stone-300 dark:bg-stone-700",
  1: "bg-red-500",
  2: "bg-amber-500",
  3: "bg-emerald-500",
  4: "bg-emerald-400",
};

/**
 * Cheap, honest strength estimate. Length carries most of the weight; the
 * remaining points reward mixing classes. Deliberately not a security
 * control — it exists to nudge people toward a passphrase.
 */
export function scorePassword(password: string): StrengthLevel {
  if (!password) return 0;

  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  // A long passphrase beats a short "complex" password, so cap the bar until
  // the minimum length is met rather than rewarding `aB3!` alone.
  if (password.length < 8) return 1;
  if (score >= 4 && password.length >= 10) return 4;
  if (score >= 3) return 3;
  if (score >= 2) return 2;
  return 1;
}

/** Requirement ticks shown under the signup / reset password fields. */
export const passwordRules = [
  { label: "8+ characters", test: (p: string) => p.length >= 8 },
  { label: "a number", test: (p: string) => /\d/.test(p) },
  { label: "a capital letter", test: (p: string) => /[A-Z]/.test(p) },
];